/**
 * ContactFormBlock
 * Props-injected: requires `siteData` and `onSubmit` props.
 * siteData: { contactInfo: { email }, siteName }
 * onSubmit: async (formData) => { ok: bool, error?: string }
 *   — caller handles the actual API call so the block stays portable.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { Section, Container, BlockTitle, BtnPrimary, t } from '../styles/shared';

const Wrap = styled.div` max-width: 600px; margin: 0 auto; `;
const Desc = styled.p` margin-bottom: 24px; color: ${t.textLight}; `;
const Fields = styled.div` display: flex; flex-direction: column; gap: 20px; margin-bottom: 24px; `;
const Group = styled.div` display: flex; flex-direction: column; gap: 6px; `;
const Label = styled.label` font-weight: 500; font-size: 0.9375rem; color: ${t.text}; `;
const Req = styled.span` color: #dc3545; `;

const inputStyle = `
  padding: 12px 16px; border: 1px solid ${t.border}; border-radius: 6px;
  font-family: ${t.fontBody}; font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus { outline: none; border-color: ${t.primary}; box-shadow: 0 0 0 3px rgba(74,103,65,0.1); }
`;

const Input = styled.input`${inputStyle}`;
const Textarea = styled.textarea`${inputStyle} resize: vertical; min-height: 120px;`;
const Select = styled.select`${inputStyle}`;

const ErrorBox = styled.div`
  background: #fee; border: 1px solid #dc3545; border-radius: 6px;
  padding: 12px 16px; margin-bottom: 20px; color: #dc3545;
`;

const Success = styled.div`
  text-align: center; padding: 48px 24px;
  svg { width: 64px; height: 64px; color: ${t.primary}; margin-bottom: 16px; }
  h3 { margin: 0 0 8px 0; font-size: 1.5rem; }
  p  { margin: 0; color: ${t.textLight}; }
`;

const defaultFields = [
  { type: 'text',     name: 'firstName', label: 'First Name', required: true },
  { type: 'text',     name: 'lastName',  label: 'Last Name',  required: true },
  { type: 'email',    name: 'email',     label: 'Email',      required: true },
  { type: 'textarea', name: 'message',   label: 'Message',    required: true },
];

const ContactFormBlock = ({ content = {}, settings = {}, siteData = {}, onSubmit }) => {
  const {
    title = 'Send a Message', description = '',
    submitButtonText = 'Send Message',
    successMessage = 'Thank you! Your message has been sent.',
    fields = defaultFields
  } = content;
  const { backgroundColor = '', padding = 'large' } = settings;

  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSubmit) return;
    setSubmitting(true); setError(null);
    try {
      const result = await onSubmit({ ...formData, recipientEmail: content.recipientEmail || siteData.contactInfo?.email, siteName: siteData.siteName });
      if (result?.ok) { setSubmitted(true); setFormData({}); }
      else setError(result?.error || 'Failed to send. Please try again.');
    } catch { setError('Failed to send. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const renderField = (field) => {
    const { type, name, label, required, options = [], placeholder = '' } = field;
    const val = formData[name] || '';
    const lbl = <Label htmlFor={name}>{label}{required && <Req> *</Req>}</Label>;
    if (type === 'textarea') return <Group key={name}>{lbl}<Textarea id={name} name={name} value={val} onChange={handleChange} required={required} placeholder={placeholder} rows={5} /></Group>;
    if (type === 'select')   return <Group key={name}>{lbl}<Select id={name} name={name} value={val} onChange={handleChange} required={required}><option value="">Select…</option>{options.map((o, i) => <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}</Select></Group>;
    return <Group key={name}>{lbl}<Input type={type} id={name} name={name} value={val} onChange={handleChange} required={required} placeholder={placeholder} /></Group>;
  };

  if (submitted) return (
    <Section $padding={padding} $bgColor={backgroundColor}>
      <Container><Success>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <h3>Thank You!</h3><p>{successMessage}</p>
      </Success></Container>
    </Section>
  );

  return (
    <Section $padding={padding} $bgColor={backgroundColor}>
      <Container>
        <Wrap>
          {title && <BlockTitle>{title}</BlockTitle>}
          {description && <Desc>{description}</Desc>}
          {error && <ErrorBox><p>{error}</p></ErrorBox>}
          <form onSubmit={handleSubmit}>
            <Fields>{fields.map(renderField)}</Fields>
            <BtnPrimary type="submit" disabled={submitting} $size="lg">
              {submitting ? 'Sending…' : submitButtonText}
            </BtnPrimary>
          </form>
        </Wrap>
      </Container>
    </Section>
  );
};

export default ContactFormBlock;
